import { isParaStudentMarkedExempt } from '../utils/para-reporting-roster.util';

/** True when the roster row has a concrete next LMS challenge (not complete / missing). */
export function hasParaLmsAssessableItem(studentOrRow: unknown): boolean {
	if (studentOrRow == null || typeof studentOrRow !== 'object') {
		return false;
	}
	const row = studentOrRow as Record<string, unknown>;
	const chall = (row['challange'] ?? row['challenge']) as Record<string, unknown> | null | undefined;
	const nc = row['nextChallenge'] ?? chall?.['nextChallenge'];
	if (nc === 'completed') {
		return false;
	}
	return nc != null && typeof nc === 'object';
}

export interface ParaLmsStartOptions {
	/** Assessment series item is locked or read-only for this classroom. */
	seriesBlocked?: boolean;
	/** Total challenges in the assessment catalog (e.g. 15 for screener). */
	catalogChallengeCount?: number;
}

/** Screener (baseline): completion = every challenge attempted or passed, not only passing the last one. */
export function isParaScreenerStyleAssessment(assessmentId: string | null | undefined): boolean {
	if (assessmentId == null || String(assessmentId).trim() === '') {
		return false;
	}
	const id = String(assessmentId).toLowerCase();
	return id.includes('screener') && !id.includes('transform');
}

export function isParaTransformStyleAssessment(assessmentId: string | null | undefined): boolean {
	if (assessmentId == null || String(assessmentId).trim() === '') {
		return false;
	}
	return String(assessmentId).toLowerCase().includes('transform');
}

/** Distinct scored challenges from a progress-report `scores` map (composite keys only). */
export function countParaAttemptedChallenges(studentOrRow: unknown): number {
	if (studentOrRow == null || typeof studentOrRow !== 'object') {
		return 0;
	}
	const scores = (studentOrRow as Record<string, unknown>)['scores'];
	if (scores == null || typeof scores !== 'object') {
		return 0;
	}
	let n = 0;
	for (const key of Object.keys(scores as Record<string, unknown>)) {
		const k = Number(key);
		if (!Number.isFinite(k) || k < 100) {
			continue;
		}
		const cell = (scores as Record<string, unknown>)[key];
		if (cell == null || typeof cell !== 'object') {
			continue;
		}
		const sc = Number((cell as Record<string, unknown>)['score']);
		if (Number.isFinite(sc) && sc > 0) {
			n++;
		}
	}
	return n;
}

/**
 * Student finished this PARA assessment (no next challenge). Exempt and series lock are excluded.
 * Screener: complete when every catalog challenge has been attempted (score > 0), pass or fail.
 */
export function isStudentAssessmentComplete(studentOrRow: unknown, opts?: ParaLmsStartOptions): boolean {
	if (isParaStudentMarkedExempt(studentOrRow)) {
		return false;
	}
	if (studentOrRow != null && typeof studentOrRow === 'object') {
		const row = studentOrRow as Record<string, unknown>;
		if (row['assessmentComplete'] === true) {
			return true;
		}
		const target = opts?.catalogChallengeCount;
		if (target != null && target > 0 && countParaAttemptedChallenges(row) >= target) {
			return true;
		}
	}
	return !hasParaLmsAssessableItem(studentOrRow);
}

export function canStartParaLmsAssessment(studentOrRow: unknown, opts?: ParaLmsStartOptions): boolean {
	if (opts?.seriesBlocked) {
		return false;
	}
	if (isParaStudentMarkedExempt(studentOrRow)) {
		return false;
	}
	if (isStudentAssessmentComplete(studentOrRow, opts)) {
		return false;
	}
	return hasParaLmsAssessableItem(studentOrRow);
}

export function getParaLmsStartBlockReason(studentOrRow: unknown, opts?: ParaLmsStartOptions): string | null {
	if (opts?.seriesBlocked) {
		return 'This assessment is locked. Complete the previous assessment for all students in the class list first.';
	}
	if (isParaStudentMarkedExempt(studentOrRow)) {
		return 'This student is exempt from assessment.';
	}
	if (isStudentAssessmentComplete(studentOrRow, opts)) {
		return 'This student has completed all challenges for this assessment.';
	}
	if (!hasParaLmsAssessableItem(studentOrRow)) {
		return 'No challenge is available to assess for this student.';
	}
	return null;
}

/** Non-exempt students who still have a challenge to assess (excludes finished / completed). */
export function filterParaLmsDrawableStudents(students: unknown[], opts?: ParaLmsStartOptions): unknown[] {
	const list = Array.isArray(students) ? students : [];
	return list.filter((s) => canStartParaLmsAssessment(s, opts));
}

export function compareParaClassStudentsByName(a: unknown, b: unknown): number {
	const an = paraClassStudentSortKey(a);
	const bn = paraClassStudentSortKey(b);
	return an.localeCompare(bn, undefined, { sensitivity: 'base' });
}

function paraClassStudentSortKey(studentOrRow: unknown): string {
	if (studentOrRow == null || typeof studentOrRow !== 'object') {
		return '';
	}
	const row = studentOrRow as Record<string, unknown>;
	const nested = row['student'];
	const st = nested != null && typeof nested === 'object' ? (nested as Record<string, unknown>) : row;
	const lname = String(st['lastName'] ?? st['lname'] ?? '').trim();
	const fname = String(st['firstName'] ?? st['fname'] ?? '').trim();
	return `${lname}\t${fname}`.toLowerCase();
}
